import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowRight, Clock, MapPin, Calendar, CheckCircle, XCircle, 
  LogIn, LogOut, AlertTriangle, Bell, RefreshCw, Navigation,
  User, Building2
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// إصلاح أيقونة الماركر الافتراضية في Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// أيقونة مخصصة للموظف
const employeeIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// أيقونة للمكتب
const officeIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-gold.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// مكون لتحريك الخريطة
function MapUpdater({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 15);
    }
  }, [center, map]);
  return null;
}

export default function Attendance() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [myRecords, setMyRecords] = useState([]);
  const [allRecords, setAllRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [location, setLocation] = useState(null);
  const [locationAddress, setLocationAddress] = useState('');
  const [activeTab, setActiveTab] = useState('today');
  const [clockOutNotes, setClockOutNotes] = useState('');
  const [showMap, setShowMap] = useState(false);
  const [mapCenter, setMapCenter] = useState([24.7136, 46.6753]); // الرياض كموقع افتراضي
  const [alerts, setAlerts] = useState([]);
  const [workSchedule, setWorkSchedule] = useState({ start: '08:00', end: '17:00' });
  const [selectedRecord, setSelectedRecord] = useState(null);

  // موقع المكتب (يمكن تعديله)
  const officeLocation = { lat: 24.7136, lng: 46.6753, name: 'مكتب المحامي هشام يوسف الخياط' };

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setUser(JSON.parse(userStr));
    }
    fetchData();
    getCurrentLocation();
    checkAlerts();
  }, []);

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setLocation({
            latitude: lat,
            longitude: lng,
            address: ''
          });
          setMapCenter([lat, lng]);
          
          // محاولة الحصول على عنوان الموقع
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=ar`
            );
            const data = await response.json();
            if (data.display_name) {
              setLocationAddress(data.display_name);
              setLocation(prev => ({ ...prev, address: data.display_name }));
            }
          } catch (err) {
            console.error('Error getting address:', err);
          }
        },
        (error) => {
          console.error('Error getting location:', error);
          setMessage('لم نتمكن من الحصول على موقعك. يرجى تفعيل خدمة الموقع.');
          setMessageType('error');
        },
        { enableHighAccuracy: true }
      );
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [todayRes, recordsRes] = await Promise.all([
        axios.get(`${API}/attendance/today`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/attendance/my-records`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setTodayAttendance(todayRes.data);
      setMyRecords(recordsRes.data);

      const userStr = localStorage.getItem('user');
      const userData = userStr ? JSON.parse(userStr) : null;
      if (userData?.role === 'admin') {
        const allRes = await axios.get(`${API}/attendance/all`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAllRecords(allRes.data);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  const checkAlerts = () => {
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    const [startHour, startMin] = workSchedule.start.split(':').map(Number);
    const [endHour, endMin] = workSchedule.end.split(':').map(Number);
    const startTime = startHour * 60 + startMin;
    const endTime = endHour * 60 + endMin;
    
    const newAlerts = [];
    
    // تنبيه التأخر (أكثر من 15 دقيقة بعد بداية الدوام)
    if (currentTime > startTime + 15 && !todayAttendance?.clock_in) {
      newAlerts.push({
        type: 'late',
        message: 'تأخرت عن موعد بداية الدوام!',
        severity: 'warning'
      });
    }
    
    // تنبيه اقتراب نهاية الدوام
    if (currentTime >= endTime - 30 && currentTime < endTime && todayAttendance?.clock_in && !todayAttendance?.clock_out) {
      newAlerts.push({
        type: 'end_soon',
        message: 'اقترب موعد نهاية الدوام. لا تنسَ تسجيل الانصراف.',
        severity: 'info'
      });
    }
    
    // تنبيه تجاوز وقت العمل
    if (currentTime > endTime + 60 && todayAttendance?.clock_in && !todayAttendance?.clock_out) {
      newAlerts.push({
        type: 'overtime',
        message: 'تجاوزت وقت العمل المحدد بأكثر من ساعة!',
        severity: 'warning'
      });
    }
    
    setAlerts(newAlerts);
  };

  useEffect(() => {
    const interval = setInterval(checkAlerts, 60000); // فحص كل دقيقة
    return () => clearInterval(interval);
  }, [todayAttendance, workSchedule]);

  const handleClockIn = async () => {
    if (!location) {
      setMessage('يرجى السماح بالوصول للموقع أولاً');
      setMessageType('error');
      getCurrentLocation();
      return;
    }
    
    setLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/attendance/clock-in`, {
        ...location,
        address: locationAddress
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage('تم تسجيل الحضور بنجاح ✓');
      setMessageType('success');
      fetchData();
      setShowMap(true);
    } catch (err) {
      setMessage(err.response?.data?.detail || 'حدث خطأ أثناء تسجيل الحضور');
      setMessageType('error');
    }
    setLoading(false);
  };

  const handleClockOut = async () => {
    if (!location) {
      setMessage('يرجى السماح بالوصول للموقع أولاً');
      setMessageType('error');
      getCurrentLocation();
      return;
    }
    
    setLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/attendance/clock-out`, {
        ...location,
        address: locationAddress,
        notes: clockOutNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage('تم تسجيل الانصراف بنجاح ✓');
      setMessageType('success');
      setClockOutNotes('');
      fetchData();
    } catch (err) {
      setMessage(err.response?.data?.detail || 'حدث خطأ أثناء تسجيل الانصراف');
      setMessageType('error');
    }
    setLoading(false);
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ar-SA');
  };

  const viewRecordOnMap = (record) => {
    setSelectedRecord(record);
    if (record.clock_in_location) {
      setMapCenter([record.clock_in_location.lat, record.clock_in_location.lng]);
    }
    setShowMap(true);
  };

  return (
    <div className="min-h-screen bg-sand" dir="rtl" lang="ar">
      <header className="bg-primary py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowRight className="w-6 h-6 text-white" />
            </button>
            <Logo size="md" />
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-white">الحضور والانصراف</h1>
          </div>
          <button
            onClick={getCurrentLocation}
            className="flex items-center gap-2 px-3 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-all text-sm"
          >
            <RefreshCw className="w-4 h-4" />
            تحديث الموقع
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* التنبيهات */}
        {alerts.length > 0 && (
          <div className="mb-6 space-y-2">
            {alerts.map((alert, idx) => (
              <div 
                key={idx}
                className={`p-4 rounded-lg flex items-center gap-3 ${
                  alert.severity === 'warning' 
                    ? 'bg-yellow-50 border border-yellow-200 text-yellow-800' 
                    : 'bg-blue-50 border border-blue-200 text-blue-800'
                }`}
              >
                {alert.severity === 'warning' ? (
                  <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                ) : (
                  <Bell className="w-5 h-5 flex-shrink-0" />
                )}
                <span className="font-body">{alert.message}</span>
              </div>
            ))}
          </div>
        )}

        {/* رسالة النظام */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg ${
            messageType === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {message}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* بطاقة تسجيل الحضور/الانصراف */}
          <div className="bg-white rounded-xl p-6 shadow-lg" data-testid="attendance-card">
            <div className="text-center mb-6">
              <Clock className="w-12 h-12 text-gold mx-auto mb-3" />
              <h2 className="text-lg font-heading font-bold text-primary">
                {new Date().toLocaleDateString('ar-SA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </h2>
              <p className="text-2xl font-bold text-gold mt-2">
                {new Date().toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>

            {/* حالة اليوم */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="text-center p-4 bg-green-50 rounded-lg border border-green-100">
                <LogIn className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <p className="text-xs text-gray-500 font-body">وقت الحضور</p>
                <p className="text-lg font-bold text-primary font-body">
                  {todayAttendance?.clock_in ? formatTime(todayAttendance.clock_in) : '-'}
                </p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg border border-red-100">
                <LogOut className="w-6 h-6 text-red-600 mx-auto mb-2" />
                <p className="text-xs text-gray-500 font-body">وقت الانصراف</p>
                <p className="text-lg font-bold text-primary font-body">
                  {todayAttendance?.clock_out ? formatTime(todayAttendance.clock_out) : '-'}
                </p>
              </div>
            </div>

            {/* الموقع الجغرافي */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Navigation className="w-4 h-4 text-gold" />
                <span className="text-sm font-semibold text-primary">موقعك الحالي</span>
              </div>
              {location ? (
                <div className="text-sm text-gray-600">
                  <p className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {locationAddress || `${location.latitude?.toFixed(6)}, ${location.longitude?.toFixed(6)}`}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-red-500">جاري تحديد الموقع...</p>
              )}
            </div>

            {/* أزرار التسجيل */}
            <div className="space-y-3">
              {!todayAttendance?.clock_in ? (
                <button
                  data-testid="clock-in-btn"
                  onClick={handleClockIn}
                  disabled={loading || !location}
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all disabled:opacity-50 text-lg font-bold"
                >
                  <LogIn className="w-6 h-6" />
                  تسجيل الحضور
                </button>
              ) : !todayAttendance?.clock_out ? (
                <>
                  <textarea
                    data-testid="clock-out-notes"
                    value={clockOutNotes}
                    onChange={(e) => setClockOutNotes(e.target.value)}
                    placeholder="ملاحظات عن اليوم (اختياري)"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg font-body text-sm"
                    rows={2}
                  />
                  <button
                    data-testid="clock-out-btn"
                    onClick={handleClockOut}
                    disabled={loading || !location}
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all disabled:opacity-50 text-lg font-bold"
                  >
                    <LogOut className="w-6 h-6" />
                    تسجيل الانصراف
                  </button>
                </>
              ) : (
                <div className="flex items-center justify-center gap-2 text-green-600 p-4 bg-green-50 rounded-lg">
                  <CheckCircle className="w-6 h-6" />
                  <span className="font-body font-semibold">تم تسجيل الحضور والانصراف لهذا اليوم</span>
                </div>
              )}
            </div>

            {/* إجمالي ساعات العمل */}
            {todayAttendance?.total_hours !== null && todayAttendance?.total_hours !== undefined && (
              <div className="mt-6 text-center p-4 bg-gold/10 rounded-lg">
                <p className="text-sm text-gray-500 font-body">إجمالي ساعات العمل</p>
                <p className="text-3xl font-bold text-gold">{todayAttendance.total_hours} ساعة</p>
              </div>
            )}
          </div>

          {/* الخريطة */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-primary flex items-center gap-2">
                <MapPin className="w-5 h-5 text-gold" />
                خريطة الموقع
              </h3>
              <button
                onClick={() => setShowMap(!showMap)}
                className="text-sm text-gold hover:text-gold-dark"
              >
                {showMap ? 'إخفاء' : 'إظهار'}
              </button>
            </div>
            
            {showMap && (
              <div className="h-[400px]">
                <MapContainer
                  center={mapCenter}
                  zoom={15}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapUpdater center={mapCenter} />
                  
                  {/* موقع المكتب */}
                  <Marker position={[officeLocation.lat, officeLocation.lng]} icon={officeIcon}>
                    <Popup>
                      <div className="text-center">
                        <Building2 className="w-5 h-5 mx-auto mb-1 text-gold" />
                        <strong>{officeLocation.name}</strong>
                      </div>
                    </Popup>
                  </Marker>
                  
                  {/* موقعي الحالي */}
                  {location && (
                    <Marker position={[location.latitude, location.longitude]} icon={employeeIcon}>
                      <Popup>
                        <div className="text-center">
                          <User className="w-5 h-5 mx-auto mb-1 text-green-600" />
                          <strong>موقعي الحالي</strong>
                          <p className="text-xs text-gray-500">{locationAddress}</p>
                        </div>
                      </Popup>
                    </Marker>
                  )}
                  
                  {/* موقع السجل المحدد */}
                  {selectedRecord?.clock_in_location && (
                    <Marker 
                      position={[selectedRecord.clock_in_location.lat, selectedRecord.clock_in_location.lng]}
                    >
                      <Popup>
                        <div className="text-center">
                          <LogIn className="w-5 h-5 mx-auto mb-1 text-green-600" />
                          <strong>موقع الحضور</strong>
                          <p className="text-xs">{selectedRecord.user_name}</p>
                          <p className="text-xs text-gray-500">{formatTime(selectedRecord.clock_in)}</p>
                        </div>
                      </Popup>
                    </Marker>
                  )}
                  
                  {selectedRecord?.clock_out_location && (
                    <Marker 
                      position={[selectedRecord.clock_out_location.lat, selectedRecord.clock_out_location.lng]}
                    >
                      <Popup>
                        <div className="text-center">
                          <LogOut className="w-5 h-5 mx-auto mb-1 text-red-600" />
                          <strong>موقع الانصراف</strong>
                          <p className="text-xs">{selectedRecord.user_name}</p>
                          <p className="text-xs text-gray-500">{formatTime(selectedRecord.clock_out)}</p>
                        </div>
                      </Popup>
                    </Marker>
                  )}
                </MapContainer>
              </div>
            )}
            
            {!showMap && (
              <div className="h-[400px] flex items-center justify-center bg-gray-50">
                <div className="text-center text-gray-500">
                  <MapPin className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>اضغط "إظهار" لعرض الخريطة</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* التبويبات */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('today')}
            className={`px-6 py-2 rounded-lg font-body transition-all ${
              activeTab === 'today' ? 'bg-gold text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            سجلاتي
          </button>
          {user?.role === 'admin' && (
            <button
              onClick={() => setActiveTab('all')}
              className={`px-6 py-2 rounded-lg font-body transition-all ${
                activeTab === 'all' ? 'bg-gold text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              جميع السجلات
            </button>
          )}
        </div>

        {/* جدول السجلات */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="attendance-table">
              <thead className="bg-primary text-white">
                <tr>
                  {activeTab === 'all' && <th className="px-4 py-3 text-right text-sm">الموظف</th>}
                  <th className="px-4 py-3 text-right text-sm">التاريخ</th>
                  <th className="px-4 py-3 text-center text-sm">الحضور</th>
                  <th className="px-4 py-3 text-center text-sm">الانصراف</th>
                  <th className="px-4 py-3 text-center text-sm">الساعات</th>
                  <th className="px-4 py-3 text-center text-sm">الحالة</th>
                  <th className="px-4 py-3 text-center text-sm">الخريطة</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(activeTab === 'today' ? myRecords : allRecords).map((record, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    {activeTab === 'all' && (
                      <td className="px-4 py-3 text-sm font-body">{record.user_name}</td>
                    )}
                    <td className="px-4 py-3 text-sm font-body">{record.date}</td>
                    <td className="px-4 py-3 text-center text-sm">
                      <div>
                        <span className="font-semibold text-green-600">{formatTime(record.clock_in)}</span>
                        {record.clock_in_location && (
                          <p className="text-xs text-gray-400 truncate max-w-[100px] mx-auto">
                            {record.clock_in_location.address || 'موقع محدد'}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-sm">
                      <div>
                        <span className={`font-semibold ${record.clock_out ? 'text-red-600' : 'text-gray-400'}`}>
                          {formatTime(record.clock_out)}
                        </span>
                        {record.clock_out_location && (
                          <p className="text-xs text-gray-400 truncate max-w-[100px] mx-auto">
                            {record.clock_out_location.address || 'موقع محدد'}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-bold text-gold">
                      {record.total_hours ? `${record.total_hours} س` : '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {record.clock_out ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                          <CheckCircle className="w-3 h-3" />
                          مكتمل
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-600 rounded-full text-xs">
                          <Clock className="w-3 h-3" />
                          متواجد
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {record.clock_in_location && (
                        <button
                          onClick={() => viewRecordOnMap(record)}
                          className="p-2 text-gold hover:bg-gold/10 rounded-lg transition-all"
                          title="عرض على الخريطة"
                        >
                          <MapPin className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {(activeTab === 'today' ? myRecords : allRecords).length === 0 && (
                  <tr>
                    <td colSpan={activeTab === 'all' ? 7 : 6} className="px-4 py-8 text-center text-gray-500">
                      لا توجد سجلات
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
